import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../../../lib/utils";

const tabsListVariants = cva(
  [
    "flex items-center relative w-full", // w-full 추가
    "flex items-center border-b-2 border-semantic-secondary-stroke-normal-default",
    "text-semantic-secondary-foreground-inverse-default",
  ],
  {
    variants: {
      size: {
        md: "",
        lg: "",
      },
    },
    defaultVariants: {
      size: "lg",
    },
  }
);

const tabsTriggerVariants = cva(
  [
    "inline-flex items-center justify-center",
    "relative border-b-2 border-transparent",
    "transition-all duration-200",
    "focus-visible:outline-none",
    "disabled:pointer-events-none disabled:opacity-50",
    "typography-semantic-heading-3-regular",
    "data-[state=active]:border-semantic-primary-background-normal-default",
    "data-[state=active]:text-semantic-primary-background-normal-default",
    "hover:text-semantic-primary-foreground-global-default",
    "h-[50px]",
    "p-3",
    "[&_span]:data-[state=active]:text-semantic-primary-foreground-global-default",
    "[&_span:last-child]:ml-1",
  ],
  {
    variants: {
      size: {
        md: [
          "typography-semantic-body-1-normal---bold",
          "data-[state=active]:typography-semantic-body-1-normal---bold",
        ],
        lg: [
          "typography-semantic-heading-3-regular",
          "data-[state=active]:typography-semantic-heading-3-bold",
        ],
      },
    },
    defaultVariants: {
      size: "lg",
    },
  }
);

interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof tabsTriggerVariants> {
  icon?: React.ReactNode;
  disabledUI?: boolean;
}

interface TabsProps extends TabsPrimitive.TabsProps {
  size?: "md" | "lg";
}

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  TabsProps
>(({ size = "md", ...props }, ref) => (
  <TabsPrimitive.Root ref={ref} {...props} />
));
Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsListVariants({ className }))}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, size, icon, disabledUI, ...props }, ref) => {
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        tabsTriggerVariants({ size, className }),
        disabledUI && "opacity-50 cursor-pointer hover:text-current"
      )}
      {...props}
    >
      {icon && <span className="size-4">{icon}</span>}
      <span>{props.children}</span>
    </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "ring-offset-background focus-visible:outline-none",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
